import { CSSProperties, useEffect } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  DEFAULT_JIRA_INSTANCE,
  JIRA_INSTANCES,
  JiraInstance,
  JiraInstanceValue,
  jiraPresetValue,
} from '../../../components/jira/presets';

// Scaffolder field for picking the squad's Jira instance. RJSF only lets a
// field change its own value, so a dropdown that fills two sibling fields is
// modelled as ONE field whose value is an object { instance, apiBaseUrl,
// browseBaseUrl }: choosing SaaS / On-Premise fills the two URL inputs with the
// preset, and the inputs stay editable afterwards. Mirrors PandaPreflightField.

const field: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  marginBottom: 14,
  maxWidth: 560,
};

const label: CSSProperties = {
  fontSize: 12.5,
  fontWeight: 600,
  color: 'var(--bui-fg-secondary)',
};

const control: CSSProperties = {
  fontSize: 13,
  padding: '7px 10px',
  borderRadius: 6,
  border: '1px solid var(--bui-border-2)',
  background: 'var(--bui-bg-neutral-1)',
  color: 'var(--bui-fg-primary)',
  width: '100%',
};

const hint: CSSProperties = { fontSize: 11.5, color: 'var(--bui-fg-secondary)' };

const isJiraInstance = (v: string): v is JiraInstance =>
  (JIRA_INSTANCES as string[]).includes(v);

export const JiraInstanceField = (
  props: FieldExtensionComponentProps<JiraInstanceValue>,
) => {
  const { onChange, formData } = props;

  // Seed SaaS on first render so the wizard never carries an empty object and
  // the 22 existing SaaS squads' onboarding stays a no-op choice.
  useEffect(() => {
    if (!formData?.instance) {
      onChange(jiraPresetValue(DEFAULT_JIRA_INSTANCE));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = formData ?? jiraPresetValue(DEFAULT_JIRA_INSTANCE);

  const onPickInstance = (next: string) => {
    if (isJiraInstance(next)) onChange(jiraPresetValue(next));
  };

  return (
    <div>
      <div style={field}>
        <label htmlFor="jira-instance" style={label}>
          Jira instance
        </label>
        <select
          id="jira-instance"
          style={control}
          value={value.instance}
          onChange={e => onPickInstance(e.target.value)}
        >
          {JIRA_INSTANCES.map(instance => (
            <option key={instance} value={instance}>
              {instance}
            </option>
          ))}
        </select>
        <span style={hint}>
          Picking an instance fills the two URLs below; you can still edit them.
          On-Premise polling is not yet available to the agent, but the links
          resolve correctly.
        </span>
      </div>

      <div style={field}>
        <label htmlFor="jira-browse-url" style={label}>
          Jira browse base URL (ticket links)
        </label>
        <input
          id="jira-browse-url"
          style={control}
          value={value.browseBaseUrl}
          onChange={e =>
            onChange({ ...value, browseBaseUrl: e.target.value })
          }
        />
      </div>

      <div style={field}>
        <label htmlFor="jira-api-url" style={label}>
          Jira API base URL (agent runtime)
        </label>
        <input
          id="jira-api-url"
          style={control}
          value={value.apiBaseUrl}
          onChange={e => onChange({ ...value, apiBaseUrl: e.target.value })}
        />
      </div>
    </div>
  );
};
